// ============================================================
// components/landing/LandingLayout.tsx
//
// Shared layout for public pages ("/" and the 404): a light,
// sticky header in the style of the app shell's top bar, a
// full-width main region, and a quiet footer. Pages own their
// width; pass `contained` to center simple content instead.
// ============================================================
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Building2, LogIn, LogOut, MessageCircle, Store } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { logout } from '../../actions/index';
import { useRoleCheck } from '../../hooks/use-role-check';
import type { AppDispatch, RootState } from '../../store';
import { APP_STORE_URL } from '../common/ComingSoonPage';
import { useIsAndroid } from '../../lib/android';
import { AndroidWaitListButton } from './AndroidWaitList';

const PRIVACY_URL = 'https://www.stash-app.com/privacy-policy';

const navItem =
  'inline-flex h-9 items-center gap-1.5 rounded-full px-3 text-sm font-semibold text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

function NavLink({ to, icon: Icon, label }: { to: string; icon: LucideIcon; label: string }): React.JSX.Element {
  return (
    <Link to={to} className={navItem}>
      <Icon className="h-4 w-4" aria-hidden="true" />
      <span className="hidden md:inline">{label}</span>
      <span className="sr-only md:hidden">{label}</span>
    </Link>
  );
}

function LandingHeader(): React.JSX.Element {
  const dispatch = useDispatch<AppDispatch>();
  const signedIn = useSelector((state: RootState) => Boolean(state.auth.currentUser));
  const android = useIsAndroid();
  const { isAdmin } = useRoleCheck();

  return (
    <header className="sticky top-0 z-30 border-b border-border/60 bg-background/80 backdrop-blur-xl">
      <div className="mx-auto flex h-16 w-full max-w-6xl items-center justify-between gap-3 px-4 md:px-6">
        <Link to="/" aria-label="STASH home" className="flex shrink-0 items-center rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          <img src="/stash-logo.png" alt="" width={962} height={436} className="h-8 w-auto dark:hidden" />
          <img src="/stash_logo_white.png" alt="" width={332} height={145} className="hidden h-8 w-auto dark:block" />
        </Link>

        <nav aria-label="Main" className="flex items-center gap-1">
          {signedIn && (
            <NavLink to="/app/chat" icon={MessageCircle} label="Ask STASH" />
          )}
          {isAdmin() && (
            <>
              <NavLink to="/app/communities" icon={Building2} label="Communities" />
              <NavLink to="/admin/vendor-edit" icon={Store} label="Vendor edit" />
            </>
          )}
          {signedIn ? (
            <button type="button" onClick={() => dispatch(logout())} className={navItem}>
              <LogOut className="h-4 w-4" aria-hidden="true" />
              <span>Log out</span>
            </button>
          ) : (
            <Link to="/app/login" className={navItem}>
              <LogIn className="h-4 w-4" aria-hidden="true" />
              <span>Log in</span>
            </Link>
          )}
          {android ? (
            <AndroidWaitListButton variant="header" />
          ) : (
            <a
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="ml-1 inline-flex h-9 items-center rounded-full bg-primary px-4 text-sm font-semibold text-primary-foreground shadow-sm transition-all hover:bg-[var(--stash-blue-dark)] hover:shadow-md active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              Get the app
            </a>
          )}
        </nav>
      </div>
    </header>
  );
}

function LandingFooter(): React.JSX.Element {
  return (
    <footer className="border-t border-border/60">
      <div className="mx-auto flex w-full max-w-6xl flex-col items-center justify-between gap-3 px-4 py-8 text-sm text-muted-foreground md:flex-row md:px-6">
        <div className="flex items-center gap-2">
          <img src="/stash-app-icon.png" alt="" width={1024} height={1024} className="h-6 w-6" />
          <span>Copyright © {new Date().getFullYear()} STASH Social</span>
        </div>
        <nav aria-label="Footer" className="flex items-center gap-5">
          <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer" className="transition-colors hover:text-foreground">
            Privacy
          </a>
          <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" className="transition-colors hover:text-foreground">
            iPhone app
          </a>
        </nav>
      </div>
    </footer>
  );
}

interface LandingLayoutProps {
  children: React.ReactNode;
  /** Center the page in a padded container (for simple pages like the 404). */
  contained?: boolean;
}

export function LandingLayout({ children, contained = false }: LandingLayoutProps): React.JSX.Element {
  return (
    <div className="flex min-h-screen flex-col bg-background font-sans text-foreground">
      <LandingHeader />
      <main className="flex-1">
        {contained ? (
          <div data-landing-container className="mx-auto w-full max-w-6xl px-4 py-12 md:px-6">
            {children}
          </div>
        ) : (
          children
        )}
      </main>
      <LandingFooter />
    </div>
  );
}
